// Mapeo centralizado de colores para estados
// Usa la paleta de colores del camping para mantener consistencia
// Se aplica tanto en el sitio público como en el admin

import { ThemeColors } from '../config/themes'

export type StatusType = 'available' | 'occupied' | 'maintenance' | 'blocked' | 'confirmed' | 'pending' | 'cancelled' | 'completed'

export interface StatusColorMap {
  backgroundColor: string
  textColor: string
  borderColor?: string
  lightBackground?: string
}

/**
 * Obtiene los colores para un estado específico usando la paleta del tema actual
 * @param status - El estado a mapear
 * @param colors - Los colores del tema actual (de useTheme)
 * @returns Objeto con backgroundColor, textColor, borderColor, etc.
 */
export const getStatusColors = (status: StatusType, colors: ThemeColors): StatusColorMap => {
  switch (status) {
    // Estados disponibles/positivos - usan color primario del tema
    case 'available':
    case 'confirmed':
    case 'completed':
      return {
        backgroundColor: colors.primary, // Color primario del tema actual
        textColor: '#FFFFFF',
        borderColor: colors.primary,
        lightBackground: `${colors.primary}15`
      }

    // Estados ocupados/no disponibles
    case 'occupied':
    case 'blocked':
      return {
        backgroundColor: colors.primaryDark, // Color primario oscuro (no disponible)
        textColor: '#FFFFFF',
        borderColor: colors.primaryDark,
        lightBackground: `${colors.primaryDark}15`
      }

    // Estados pendientes/en proceso
    case 'pending':
      return {
        backgroundColor: colors.primaryLight, // Color primario claro (espera/pendiente)
        textColor: colors.primaryDark,
        borderColor: colors.primaryLight,
        lightBackground: `${colors.primaryLight}30`
      }

    // Estados en mantenimiento/inactivos
    case 'maintenance':
      return {
        backgroundColor: colors.primaryDark, // Color primario oscuro del tema
        textColor: '#FFFFFF',
        borderColor: colors.primaryDark,
        lightBackground: `${colors.primaryDark}15`
      }

    // Estado cancelado
    case 'cancelled':
      return {
        backgroundColor: colors.primaryDark, // Color primario oscuro (rechazado/cancelado)
        textColor: '#FFFFFF',
        borderColor: colors.primaryDark,
        lightBackground: `${colors.primaryDark}15`
      }

    default:
      return {
        backgroundColor: colors.border,
        textColor: colors.text,
        borderColor: colors.border,
        lightBackground: `${colors.border}15`
      }
  }
}

/**
 * Obtiene el texto descriptivo para un estado
 */
export const getStatusText = (status: StatusType): string => {
  switch (status) {
    case 'available':
      return '✅ Disponible'
    case 'occupied':
      return '❌ Ocupado'
    case 'maintenance':
      return '🔧 Mantenimiento'
    case 'blocked':
      return '🚫 Bloqueado'
    case 'confirmed':
      return '✓ Confirmado'
    case 'pending':
      return '⏳ Pendiente'
    case 'cancelled':
      return '✕ Cancelado'
    case 'completed':
      return '✓ Completado'
    default:
      return 'Estado desconocido'
  }
}

/**
 * Utility para generar className de Tailwind (para casos simples sin tema dinámico)
 * DEPRECATED: Preferir getStatusColors() con useTheme para soporte dinámico
 */
export const getStatusClassName = (status: StatusType): string => {
  switch (status) {
    case 'available':
    case 'confirmed':
    case 'completed':
      return 'bg-green-500 text-white'
    case 'occupied':
    case 'blocked':
      return 'bg-red-500 text-white'
    case 'pending':
      return 'bg-yellow-500 text-white'
    case 'maintenance':
      return 'bg-gray-500 text-white'
    case 'cancelled':
      return 'bg-gray-600 text-white'
    default:
      return 'bg-gray-400 text-white'
  }
}
